/* eslint-disable no-magic-numbers */
import moment from "moment-timezone";
import mapValues from "lodash/mapValues";
import keyBy from "lodash/keyBy";
import fromPairs from "lodash/fromPairs";

function defaultMeetingState() {
	return {
		// flags
		hasActiveInitiatorEverJoinedMeeting: false,
		isCreatingTopicOutcome: false,
		isFetchingMeetingNote: false,
		isFinalizingMeeting: false,
		isJoiningMeeting: false,
		isMeetingInProgress: false,
		isMeetingNoteNew: false,
		isStartingMeeting: false,
		isStartingTopic: false,
		isStoppingMeeting: false,
		isUpdatingTopicOutcome: false,

		// state
		currentMeetingNote: "",
		meetingInitiator: "",
		pristineMeetingNote: "",
		startedTopicId: "",
		/**
		 * @type {Number}
		 */
		startTime: 0,
		stoppedTopicId: "",
		selectedTopicId: "",
		lastSelectedTopicId: "",

		// maps
		meetingAttendanceMap: {},
		meetingAttendeeMap: {},
		/**
		 * @type {Object.<topicId, Number>}
		 */
		topicStartMap: {},
		topicStartTimeMap: {},
	};
}

function patchMeetingState( { meetingId, state, ...newState } ) {
	const meetingState = state.meetingMap[ meetingId ];
	state.meetingMap = {
		...state.meetingMap,
		[ meetingId ]: {
			...defaultMeetingState(),
			...meetingState,
			...newState,
		},
	};
}

const mutations = {
	attendeesChanged: ( state, { meetingId, attendees, } ) => {
		const attendeeMap = keyBy( attendees, "personId" );

		const hasActiveInitiatorEverJoinedMeeting = attendees.reduce( ( hasJoined, attendee ) => {
			return hasJoined ||
				state.meetingMap[ meetingId ]?.hasActiveInitiatorEverJoinedMeeting ||
				attendee.personId === state.meetingMap[ meetingId ]?.meetingInitiator;
		}, false );

		patchMeetingState( {
			meetingId,
			state,
			hasActiveInitiatorEverJoinedMeeting,
			meetingAttendeeMap: attendeeMap,
			meetingAttendanceMap: {
				...mapValues( attendeeMap, attendee => !!attendee.isAttending ),
			},
		} );
	},
	meetingNoteContentUpdated: ( state, { meetingId, noteContent, } ) => {
		patchMeetingState( {
			meetingId,
			state,
			currentMeetingNote: noteContent,
		} );
	},
	meetingNoteFetched: ( state, { meetingId, note, } ) => {
		patchMeetingState( {
			meetingId,
			state,
			isMeetingNoteNew: Object.keys( note ).length === 0,
			currentMeetingNote: note.notes || "",
			pristineMeetingNote: note.notes || "",
		} );
	},
	meetingNoteSaved: ( state, { meetingId, } ) => {
		patchMeetingState( {
			meetingId,
			state,
			isMeetingNoteNew: false,
			pristineMeetingNote: state.meetingMap[ meetingId ].currentMeetingNote,
		} );
	},
	meetingStarted: ( state, { meeting, timezone, } ) => {
		const { topics = [], } = meeting;
		const topicStartTimeMap = topics.reduce( ( topicTimers, topic ) => {
			// duration is in seconds
			topicTimers[ topic.id ] = topic.duration || 0;
			return topicTimers;
		}, {} );

		const { invitees = [], } = meeting;
		const meetingInviteeMap = keyBy( invitees, "personId" );

		const currentMeetingState = state.meetingMap[ meeting.id ] || {};
		const stoppedTopicId = currentMeetingState?.startedTopicId;

		patchMeetingState( {
			meetingId: meeting.id,
			state,
			isMeetingInProgress: true,
			startTime: moment.tz( meeting.actualStart * 1000, timezone ).unix(),
			startedTopicId: meeting.currentTopicId || "",
			stoppedTopicId,
			topicStartTimeMap,
			meetingAttendanceMap: {
				...mapValues( meetingInviteeMap, () => false ),
				...currentMeetingState?.meetingAttendanceMap,
			},
			selectedTopicId: currentMeetingState?.selectedTopicId || "",
		} );

		if ( currentMeetingState.startedTopicId ) {
			patchMeetingState( {
				meetingId: meeting.id,
				state,
				topicStartMap: {
					...currentMeetingState.topicStartMap,
					...fromPairs( topics.map( topic => [ topic.id, !!topic.duration, ] ) ),
					[ currentMeetingState.startedTopicId ]: true,
				},
			} );
		}
	},
	meetingStateUpdated: ( state, { meetingId, ...newMeetingState } ) => {
		patchMeetingState( {
			meetingId,
			state,
			...newMeetingState,
		} );
	},
	meetingStopped: ( state, { meeting, } ) => {
		patchMeetingState( {
			meetingId: meeting.id,
			state,
			isMeetingInProgress: false,
			hasActiveInitiatorEverJoinedMeeting: false,
			meetingInitiator: "",
			startTime: 0,
			startedTopicId: "",
			stoppedTopicId: "",
			topicStartMap: {},
			topicStartTimeMap: {},
			meetingAttendeeMap: {},
			meetingAttendanceMap: {},
		} );
	},
	meetingTopicCreated: ( state, { meetingId, createdTopic, } ) => {
		const meetingState = state.meetingMap[ meetingId ];
		if ( meetingState?.isMeetingInProgress ) {
			patchMeetingState( {
				meetingId,
				state,
				topicStartTimeMap: {
					...state.meetingMap[ meetingId ].topicStartTimeMap,
					[ createdTopic.id ]: ( createdTopic.duration || 0 ),
				},
			} );
		}
	},
	topicDurationUpdated: ( state, { meetingId, seconds = 0, } ) => {
		const { startedTopicId, topicStartTimeMap, } = state.meetingMap[ meetingId ];
		if ( !startedTopicId ) {
			return;
		}
		const previousDuration = topicStartTimeMap[ startedTopicId ] || 0;
		patchMeetingState( {
			meetingId,
			state,
			topicStartTimeMap: {
				...topicStartTimeMap,
				[ startedTopicId ]: previousDuration + Math.abs( seconds ),
			},
		} );
	},
	topicSelected: ( state, { meetingId, topicId, } ) => {
		const { selectedTopicId, } = ( state.meetingMap[ meetingId ] || {} );
		patchMeetingState( {
			meetingId,
			state,
			selectedTopicId: topicId,
			lastSelectedTopicId: selectedTopicId || "",
		} );
	},
};

export default {
	...mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/meeting/mutations.js